/**
 * One file per resource (plan §2). Extended additively by blocks 04, 07, 19
 * and 37 — none of them adds a file, and none of them touches `http.ts`.
 */
import { captureResultSchema, nookListSchema, nookSchema, nookSiteListSchema, summaryStatusSchema, } from "@nook/schema";
export function listNooks(http, options = {}) {
    const params = new URLSearchParams();
    // Arrays repeat the key (`?source=a&source=b`), which is what the route reads.
    for (const [key, value] of Object.entries(options)) {
        for (const item of [value].flat()) {
            if (item !== undefined && item !== null)
                params.append(key, String(item));
        }
    }
    const query = params.toString();
    return http.request({
        path: `/v1/nooks${query ? `?${query}` : ""}`,
        schema: nookListSchema,
    });
}
export function listNookSites(http) {
    return http.request({ path: "/v1/nooks/sites", schema: nookSiteListSchema });
}
export function getNook(http, id) {
    return http.request({ path: `/v1/nooks/${id}`, schema: nookSchema });
}
/**
 * The only write a nook takes after capture (block 37).
 *
 * No `Idempotency-Key`: a `PATCH` carrying an absolute value is a set, not an
 * append, so a replayed request is the same request — which is why the hook
 * above it can retry without a key's help.
 */
export function updateNook(http, id, body) {
    return http.request({
        path: `/v1/nooks/${id}`,
        method: "PATCH",
        body,
        schema: nookSchema,
    });
}
/**
 * Regenerate this nook's summary (block 14).
 *
 * `202` and a flag: the summary is written by a worker, so the answer to this
 * request is that one is coming. The text arrives through `getNook`, which the
 * detail query is already polling while `summaryPending` is true.
 */
export function regenerateSummary(http, id) {
    return http.request({
        path: `/v1/nooks/${id}/summary/regenerate`,
        method: "POST",
        schema: summaryStatusSchema,
    });
}
/**
 * `DELETE` answers `204` with no body, so there is no shape for `@nook/schema`
 * to own — the parser is the structural `Parser<void>` `http.ts` already types
 * against, not a Zod schema this package would have to import `zod` to build.
 */
const noContent = { parse: () => undefined };
/**
 * Always resolves for a well-formed request: the backend answers `204` whether
 * or not a row was there, because delete is idempotent (block 04 override 3).
 */
export function deleteNook(http, id) {
    return http.request({ path: `/v1/nooks/${id}`, method: "DELETE", schema: noContent });
}
/**
 * The key is a parameter, not something generated in here.
 *
 * A key minted per *call* would be minted again on every retry, which makes
 * idempotency decorative — the thing this block's spec warns about by name.
 * `useCreateNook` owns the key's lifetime because it is the only layer that
 * knows what one attempt is.
 */
export function createNook(http, payload, idempotencyKey) {
    return http.request({
        path: "/v1/nooks",
        method: "POST",
        body: payload,
        schema: captureResultSchema,
        headers: { "idempotency-key": idempotencyKey },
    });
}
//# sourceMappingURL=nooks.js.map